import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@hooks/useAppDispatch';
import useAbortController from '@hooks/useAbortController';
import useTranslation from 'next-translate/useTranslation';
import { getHomeDeliverySlots, getPickUpInStoreSlots } from '@api/interfaces/cartApi';
import useCustomer from '@hooks/useCustomer';
import { trackDeliveryDate, trackDeliverySelectedTime } from '@helpers/analyticsHelpers/trackDeliveryWidget';
import paths from '@constants/paths';
import Config from '@config';
import Link from '@atoms/Link/Link';
import Spinner from '@molecules/Spinner/Spinner';
import TimeSlotDetails from '@molecules/TimeSlotDetails/TimeSlotDetails';
import TimeSlots from '@molecules/TimeSlots/TimeSlots';
import SlotAddressChanger from '@molecules/SlotAddressChanger/SlotAddressChanger';
import SlotDatePicker from '@molecules/SlotDatePicker/SlotDatePicker';
import logger from '@logger';
import useCustomRouter from '@hooks/useCustomRouter';
import { selectDeliveryPickerProceedToCheckout } from '@slices/deliveryPickerSlice';
import { dateToNumeric } from '@helpers/date';
import type {
  AxfoodCartViewModel,
  AxfoodStoreInfoViewModel,
  AxfoodSlotsScheduleViewModel,
  AxfoodTimeSlotViewModel,
} from '@occ/api-client';
import {
  StyledAlert,
  StyledButton,
  StyledButtonContainer,
  StyledLoadingContainer,
  StyledLoadingText,
  StyledSlotFooter,
  StyledSlotSelector,
  StyledSlotSelectorWrapper,
} from './SlotSelector.styles';
import useStore from '@hooks/useStore';
import useCart from '@hooks/useCart';
import useCartActions from '@hooks/useCartActions';
import { getDeliveryAddress } from '@helpers/deliveryPickerHelper';
import DeliveryModeCode from '@enums/DeliveryModeCode';
import Text from '@atoms/Text/Text';

interface Props {
  theme?: string;
  changeDeliveryHandler?: () => void;
  submitButtonText?: string;
  cancelButtonText?: string;
  onSubmitted?: () => void;
  onCancel?: () => void;
  deliveryMode?: 'homeDelivery' | 'pickUpInStore';
}

type SlotAddress = {
  line1: string;
  line2?: string;
};

const SlotSelector = ({
  onSubmitted,
  onCancel,
  submitButtonText,
  cancelButtonText,
  theme = 'deliveryPicker',
  changeDeliveryHandler,
  deliveryMode,
}: Props) => {
  const dispatch = useAppDispatch();
  const { cart } = useCart();
  const { updateSlotInCart } = useCartActions();
  const proceedToCheckout = useAppSelector(selectDeliveryPickerProceedToCheckout);
  const { store } = useStore();
  const [slotData, setSlotData] = useState<AxfoodSlotsScheduleViewModel | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState<AxfoodTimeSlotViewModel | null>(null);
  const [selectedDate, setSelectedDate] = useState<DayDate | null>(null);
  const [isPreselected, setIsPreselected] = useState(true);
  const [addressData, setAddressData] = useState<SlotAddress | null>(null);
  const [showExternalPickupLocationNotice, setShowExternalPickupLocationNotice] = useState(false);
  const { customer } = useCustomer();
  const { t } = useTranslation('slotSelector');
  const timeSlotsRef = useRef<HTMLDivElement | null>(null);

  const getSignal = useAbortController();
  const homeDelivery = useMemo(() => cart?.deliveryModeCode === Config.HOME_DELIVERY || false, [cart?.deliveryModeCode]);
  const pickUpInStore = useMemo(
    () => cart?.deliveryModeCode === Config.PICKUP_IN_STORE || false,
    [cart?.deliveryModeCode]
  );
  const disableChangeDeliveryMethod = useMemo(
    () => Boolean(cart?.orderReference) && cart?.franchiseStore,
    [cart?.franchiseStore, cart?.orderReference]
  );
  const router = useCustomRouter();
  const isCheckout = useMemo(() => router.pathname === paths.CHECKOUT, [router.pathname]);
  const [waitForSubmit, setWaitForSubmit] = useState(false);

  const filteredSlots = slotData?.slots
    ? slotData?.slots?.filter((slot) => selectedDate?.groupKey === dateToNumeric(slot.startTime || 0))
    : [];

  const getSlots = useCallback(() => {
    const signal = getSignal();
    if (homeDelivery) {
      return getHomeDeliverySlots(cart?.deliveryAddress?.postalCode as string, customer?.isB2BCustomer, signal);
    }
    return getPickUpInStoreSlots(store?.storeId || '', customer?.isB2BCustomer, signal);
  }, [cart?.deliveryAddress, customer?.isB2BCustomer, homeDelivery, store?.storeId, getSignal]);

  const buttonActive =
    selectedSlot && selectedSlot?.startTime && dateToNumeric(selectedSlot?.startTime) === selectedDate?.groupKey;

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (homeDelivery || pickUpInStore) {
          setIsLoading(true);
          await getSlots()
            .then((r) => {
              const respData = r.data as AxfoodSlotsScheduleViewModel;
              setSlotData(respData);
              setSelectedSlot(respData.selectedSlot || null);
              setShowExternalPickupLocationNotice(respData.showExternalPickupLocationNotice);
            })
            .catch((e) => {
              if (e instanceof Error) {
                logger.error({ error: 'Could not get slots', message: e.message });
              }
            })
            .finally(() => {
              setIsLoading(false);
            });
        }
      } catch (error) {
        if (error instanceof Error) {
          logger.error({ error: 'Failed to getSlots', message: error.message });
        }
      }
    };

    fetchData();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const address = getDeliveryAddress({
      deliveryModeCode: cart?.deliveryModeCode as DeliveryModeCode,
      store: store as AxfoodStoreInfoViewModel,
      cart: cart as AxfoodCartViewModel,
    });
    if (!address && changeDeliveryHandler) changeDeliveryHandler();
    setAddressData(address);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedDate && !isPreselected) {
      trackDeliveryDate(homeDelivery, filteredSlots, isCheckout);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  useEffect(() => {
    if (selectedSlot) {
      trackDeliverySelectedTime(homeDelivery, isCheckout);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSlot]);

  const onSubmit = useCallback(async () => {
    if (!selectedSlot) return;
    setWaitForSubmit(true);

    await updateSlotInCart(
      encodeURIComponent(selectedSlot.code),
      !!slotData?.tmsSlots,
      selectedSlot?.tmsDeliveryWindowReference,
      homeDelivery,
      isCheckout
    );

    setWaitForSubmit(false);
    if (onSubmitted) onSubmitted();
    if (proceedToCheckout && router.pathname?.startsWith(paths.CART)) {
      router.push(`${paths.CHECKOUT}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dispatch,
    homeDelivery,
    isCheckout,
    onSubmitted,
    proceedToCheckout,
    router.pathname,
    selectedSlot,
    slotData?.tmsSlots,
  ]);

  return (
    <StyledSlotSelector>
      <SlotAddressChanger
        changeDeliveryHandler={changeDeliveryHandler}
        address={addressData}
        disableChangeDeliveryMethod={disableChangeDeliveryMethod}
      />
      {isLoading ? (
        <StyledLoadingContainer>
          <StyledLoadingText type="subhead">{t('loading')}</StyledLoadingText>
          <Spinner size={32} />
        </StyledLoadingContainer>
      ) : (
        <StyledSlotSelectorWrapper showFooter={!!buttonActive}>
          {showExternalPickupLocationNotice && !store?.externalPickupLocation && !cart?.franchiseStore && (
            <StyledAlert id="externalNotice" theme="permanent" hasCloseButton={false}>
              <span>{t('externalNotice')}</span>
              <Link href={paths.PICKUP_STORES} target="_blank" rel="noreferrer">
                {t('showAllPickupLocations')}
              </Link>
            </StyledAlert>
          )}
          {cart?.franchiseStore && (
            <StyledAlert id="franchiseNotice" theme="permanent" hasCloseButton={false}>
              <Text type="body" size="small">
                {t('franchiseNotice')}
              </Text>
            </StyledAlert>
          )}
          {slotData && (
            <SlotDatePicker
              slotData={slotData}
              daysAhead={3}
              onSelectDate={(date, preselected) => {
                setSelectedDate(date);
                setIsPreselected(preselected);

                if (timeSlotsRef.current) {
                  setTimeout(() => {
                    const buttonElement =
                      timeSlotsRef.current?.querySelector<HTMLButtonElement>('button:not([disabled])');
                    buttonElement?.focus();
                  }, 0);
                }
              }}
              selectedDate={selectedDate}
            />
          )}
          <TimeSlots
            deliveryMode={deliveryMode}
            slots={filteredSlots}
            onSlotClick={setSelectedSlot}
            selectedSlot={selectedSlot}
            ref={timeSlotsRef}
          />
          {buttonActive && (
            <StyledSlotFooter>
              {selectedSlot && <TimeSlotDetails slot={selectedSlot} />}
              <StyledButtonContainer theme={theme}>
                {cancelButtonText && onCancel && (
                  <StyledButton theme="secondary" onClick={onCancel} disabled={!buttonActive}>
                    {cancelButtonText}
                  </StyledButton>
                )}
                <StyledButton
                  onClick={onSubmit}
                  data-testid="timeslot-submit"
                  disabled={!buttonActive}
                  isSubmitting={waitForSubmit}
                >
                  {submitButtonText || t('confirmSlot')}
                </StyledButton>
              </StyledButtonContainer>
            </StyledSlotFooter>
          )}
        </StyledSlotSelectorWrapper>
      )}
    </StyledSlotSelector>
  );
};

export default SlotSelector;
